/**
 * IconToggle — iter-213.5
 *
 * Icon-only square button primitive. Use for "X" close handles, gear
 * settings, dismiss buttons, swap toggles — anything that renders one
 * icon and nothing else.
 *
 * Why this exists when `<Button size="icon">` is already available:
 *   The shadcn icon button is 40×40 with `[&_svg]:size-4` enforced.
 *   That's too big for a row affordance (cross next to a tag, swap
 *   arrow between two amounts) and too rigid (you can't render a
 *   `h-3 w-3` icon without overriding two utility groups). The
 *   IconToggle is 24/28/32, leaves SVG sizing to the caller, and
 *   inherits the same focus + disabled behaviour as Chip.
 *
 * Variants
 * --------
 *   default  — slate-100 background with subtle hover
 *   ghost    — transparent until hover (toolbars, cards)
 *   danger   — rose tone for destructive affordances (remove tag)
 *
 * Sizes
 * -----
 *   xs — 20×20 (super-compact rows)
 *   sm — 24×24 (default)
 *   md — 28×28
 *   lg — 32×32
 *
 * Always include `aria-label` (it doubles as the tooltip via `title`).
 *
 * Example
 * -------
 *   <IconToggle aria-label="Quitar tag" variant="ghost" size="xs" onClick={...}>
 *     <X className="h-2.5 w-2.5" />
 *   </IconToggle>
 */
import React from "react";
import { cn } from "../../lib/utils";

const VARIANT_CLASSES = {
  default: "bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200",
  ghost: "bg-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-700 border-transparent",
  danger: "bg-transparent text-rose-600 hover:bg-rose-50 border-transparent",
  selected: "bg-slate-900 text-white hover:bg-slate-800 border-slate-900",
};

const SIZE_CLASSES = {
  xs: "h-5 w-5",
  sm: "h-6 w-6",
  md: "h-7 w-7",
  lg: "h-8 w-8",
};

export const IconToggle = React.forwardRef(function IconToggle(
  {
    children,
    variant = "default",
    size = "sm",
    onClick,
    className,
    type = "button",
    disabled = false,
    "aria-label": ariaLabel,
    title,
    "data-testid": dataTestId,
    ...rest
  },
  ref,
) {
  const variantCls = VARIANT_CLASSES[variant] || VARIANT_CLASSES.default;
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  return (
    <button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title || ariaLabel}
      data-testid={dataTestId}
      className={cn(
        "inline-flex items-center justify-center rounded-full border",
        "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        sizeCls,
        variantCls,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
